import {
  GridColDef,
  GridRenderCellParams,
} from '@explorer/components/design-system';
import { IconButton, Typography } from '@explorer/core';
import { truncateText } from '@explorer/helpers';
import {
  ControlEscalationPriority,
  ControlUserContactInfoResponse,
} from '@explorer/open-api-client';
import EditIcon from '@mui/icons-material/Edit';
import { useIntl } from 'react-intl';

export type EscalationContactRow = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  escalation_priority: ControlEscalationPriority;
  contact: ControlUserContactInfoResponse;
};

type GridRenderCellParamsString = GridRenderCellParams<
  string,
  EscalationContactRow
>;

type GridRenderCellParamsPriority = GridRenderCellParams<
  ControlEscalationPriority,
  EscalationContactRow
>;

type GridRenderCellParamsContact = GridRenderCellParams<
  ControlUserContactInfoResponse,
  EscalationContactRow
>;

interface GetColumnsParams {
  onEditContact: (contact: ControlUserContactInfoResponse) => void;
  isLoading: boolean;
}

const getEscalationPriorityLabel = (
  priority: ControlEscalationPriority,
  intl: ReturnType<typeof useIntl>,
): string => {
  switch (priority) {
    case ControlEscalationPriority.TierOneAllAlerts:
      return intl.formatMessage({
        id: 'mdr.escalation-contacts.priority.primary',
      });
    case ControlEscalationPriority.TierTwoBackupAlerts:
      return intl.formatMessage({
        id: 'mdr.escalation-contacts.priority.backup',
      });
    case ControlEscalationPriority.None:
      return intl.formatMessage({
        id: 'mdr.escalation-contacts.priority.none',
      });
    default:
      return '-';
  }
};

export const GetColumns = ({
  onEditContact,
  isLoading,
}: GetColumnsParams): GridColDef<EscalationContactRow>[] => {
  const intl = useIntl();

  return [
    {
      field: 'email',
      headerName: intl.formatMessage({
        id: 'mdr.escalation-contacts.table.email',
      }),
      sortable: true,
      minWidth: 200,
      flex: 3,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParamsString) => (
        <Typography variant="body2">
          {truncateText(params.value, 40)}
        </Typography>
      ),
    },
    {
      field: 'first_name',
      headerName: intl.formatMessage({
        id: 'mdr.escalation-contacts.table.first-name',
      }),
      sortable: true,
      minWidth: 150,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParamsString) => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
    {
      field: 'last_name',
      headerName: intl.formatMessage({
        id: 'mdr.escalation-contacts.table.last-name',
      }),
      sortable: true,
      minWidth: 150,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParamsString) => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
    {
      field: 'phone',
      headerName: intl.formatMessage({
        id: 'mdr.escalation-contacts.table.phone',
      }),
      sortable: true,
      minWidth: 150,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParamsString) => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
    {
      field: 'escalation_priority',
      headerName: intl.formatMessage({
        id: 'mdr.escalation-contacts.table.priority',
      }),
      sortable: true,
      minWidth: 150,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParamsPriority) => (
        <Typography variant="body2">
          {getEscalationPriorityLabel(params.value, intl)}
        </Typography>
      ),
    },
    {
      field: 'contact',
      headerName: intl.formatMessage({
        id: 'mdr.escalation-contacts.table.actions',
      }),
      sortable: false,
      minWidth: 80,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParamsContact) => (
        <IconButton
          size="small"
          onClick={() => onEditContact(params.value)}
          disabled={isLoading}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];
};
